/**
 * Maps Ascend Health Plus (AHP)'s inbound session payload into the canonical
 * `Session` domain model.
 *
 * AHP session shape (POST /api/v1/sessions — body is an ARRAY of these):
 *   {
 *     uuid,
 *     provider: { email, first_name, last_name },
 *     member:   { email, first_name, last_name },
 *     location,
 *     start, end,            // "2020-11-20 07:30:00 +0800" (local time + offset)
 *     status,                // requested | confirmed | cancelled | completed
 *     notes,
 *     service_type,
 *     face_to_face,
 *     non_face_to_face_session_type,
 *     checkins: [{ kind, occurred_at, latitude, longitude }]
 *   }
 *
 * Rules (locked product decisions):
 * - The canonical key is `uuid`; everything is keyed on it. Missing/empty → invalid.
 * - `start`/`end` are parsed to epoch ms; the UTC offset is preserved in `timezone`.
 * - `face_to_face === false` (or a non-empty `non_face_to_face_session_type`) means telehealth.
 * - Be defensive about missing/null fields.
 */

import { hasTelehealthSubType, isFaceToFace } from './telehealth'
import type { CoreSessionStatus, Session, SessionCheckin } from './types'

export { hasTelehealthSubType, isFaceToFace, telehealthKind } from './telehealth'
export type { FaceToFaceLike, TelehealthKind } from './telehealth'

/** A nested person object (`provider`/`member`) on the AHP payload. */
export interface RawAhpPerson {
  email?: string | null
  first_name?: string | null
  last_name?: string | null
}

/** A single check-in/out record on the AHP payload. */
export interface RawAhpCheckin {
  kind?: string | null
  occurred_at?: string | null
  latitude?: number | string | null
  longitude?: number | string | null
}

/** Shape of a single session object as received from AHP. */
export interface RawCoreSession {
  /** Canonical session identifier. Required. */
  uuid?: string | null
  provider?: RawAhpPerson | null
  member?: RawAhpPerson | null
  location?: string | null
  start?: string | null
  end?: string | null
  status?: string | null
  notes?: string | null
  service_type?: string | null
  /** AHP sends a boolean, but tolerate 'false' / 0 / '0' from loose JSON. */
  face_to_face?: boolean | string | number | null
  non_face_to_face_session_type?: string | null
  checkins?: RawAhpCheckin[] | null
  /**
   * Nullable link to a recurring series. Not part of AHP's documented shape but
   * accepted (and mapped to `seriesUuid`) if present for forward-compat.
   */
  session_uuid?: string | null
  /** Legacy integer id — IGNORED. Do not map into the domain. */
  id?: number
}

/** Thrown when a raw item cannot be mapped (e.g. missing `uuid`). */
export class SessionMappingError extends Error {
  constructor(
    message: string,
    readonly code: 'MISSING_UUID' | 'INVALID_DATE',
  ) {
    super(message)
    this.name = 'SessionMappingError'
  }
}

/**
 * AHP datetime format: "YYYY-MM-DD HH:mm:ss +HHMM" (local time + offset). Also
 * tolerates an ISO8601 string (with offset or "Z") for forward-compat.
 */
const AHP_DATETIME_RE =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.\d+)?\s*([+-]\d{2}):?(\d{2})$/
const ISO_OFFSET_RE = /([+-]\d{2}:?\d{2}|Z)$/

interface ParsedDateTime {
  epochMs: number
  /** Normalised UTC offset, e.g. "+08:00" / "-05:00" / "+00:00", or "" if absent. */
  offset: string
}

/** Parse an AHP/ISO datetime into epoch ms plus its normalised UTC offset. */
function parseDateTime(value: string | null | undefined, field: string): ParsedDateTime {
  if (!value || typeof value !== 'string') {
    throw new SessionMappingError(`Missing ${field} timestamp`, 'INVALID_DATE')
  }
  const trimmed = value.trim()

  const ahp = AHP_DATETIME_RE.exec(trimmed)
  if (ahp) {
    const [, date, time, offHours, offMinutes] = ahp
    const offset = `${offHours}:${offMinutes}`
    const iso = `${date}T${time}${offset}`
    const ms = Date.parse(iso)
    if (Number.isNaN(ms)) {
      throw new SessionMappingError(`Unparseable ${field} timestamp: ${value}`, 'INVALID_DATE')
    }
    return { epochMs: ms, offset }
  }

  // Fallback: ISO8601 (or anything Date.parse understands with an explicit offset).
  const ms = Date.parse(trimmed)
  if (Number.isNaN(ms)) {
    throw new SessionMappingError(`Unparseable ${field} timestamp: ${value}`, 'INVALID_DATE')
  }
  const isoOffset = ISO_OFFSET_RE.exec(trimmed)?.[1]
  let offset = ''
  if (isoOffset === 'Z') offset = '+00:00'
  else if (isoOffset) offset = isoOffset.includes(':') ? isoOffset : `${isoOffset.slice(0, 3)}:${isoOffset.slice(3)}`
  return { epochMs: ms, offset }
}

function normalizeStatus(raw: string | null | undefined): CoreSessionStatus {
  switch ((raw ?? '').toLowerCase()) {
    case 'requested':
      return 'requested'
    case 'confirmed':
      return 'confirmed'
    case 'completed':
      return 'completed'
    case 'cancelled':
    case 'canceled':
      return 'cancelled'
    default:
      return 'unknown'
  }
}

/**
 * Derive the initial lifecycle state from the core status. A session AHP already
 * considers `completed` starts at `checked-out`; everything else starts at
 * `scheduled`. The state machine governs all subsequent transitions.
 */
function deriveInitialLifecycle(status: CoreSessionStatus): Session['lifecycleState'] {
  if (status === 'completed') return 'checked-out'
  if (status === 'cancelled') return 'synced'
  return 'scheduled'
}

function trimOrEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : ''
}

/** Build a display name from `first_name` + `last_name`. */
function fullName(person: RawAhpPerson | null | undefined): string {
  return [trimOrEmpty(person?.first_name), trimOrEmpty(person?.last_name)]
    .filter(Boolean)
    .join(' ')
}

function toNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return 0
}

/** Map AHP's `checkins[]` into the domain shape (best-effort; skips bad rows). */
function mapCheckins(raw: RawAhpCheckin[] | null | undefined): SessionCheckin[] {
  if (!Array.isArray(raw)) return []
  const out: SessionCheckin[] = []
  for (const c of raw) {
    if (!c) continue
    let occurredAt = 0
    try {
      occurredAt = parseDateTime(c.occurred_at, 'checkin.occurred_at').epochMs
    } catch {
      // A checkin with an unparseable timestamp is non-fatal — keep the rest.
      occurredAt = 0
    }
    out.push({
      kind: trimOrEmpty(c.kind),
      occurredAt,
      lat: toNumber(c.latitude),
      lng: toNumber(c.longitude),
    })
  }
  return out
}

/**
 * Map a single raw AHP session into the domain model.
 * @throws {SessionMappingError} when `uuid` is missing or start/end are invalid.
 */
export function mapCoreSessionToDomain(raw: RawCoreSession): Session {
  const uuid = trimOrEmpty(raw?.uuid)
  if (!uuid) {
    throw new SessionMappingError('AHP session is missing the canonical `uuid`', 'MISSING_UUID')
  }

  const coreStatus = normalizeStatus(raw.status)
  // Default to face-to-face unless AHP explicitly says otherwise.
  const faceToFace = isFaceToFace({
    faceToFace: raw.face_to_face,
    nonFaceToFaceSessionType: raw.non_face_to_face_session_type,
  })

  const start = parseDateTime(raw.start, 'start')
  const end = parseDateTime(raw.end, 'end')

  return {
    uuid,
    seriesUuid: trimOrEmpty(raw.session_uuid) || null,
    providerEmail: trimOrEmpty(raw.provider?.email),
    providerName: fullName(raw.provider),
    // Lowercased: it keys the member GSI and every follower/link lookup.
    memberEmail: trimOrEmpty(raw.member?.email).toLowerCase(),
    memberName: fullName(raw.member),
    locationText: trimOrEmpty(raw.location),
    geo: null,
    serviceType: trimOrEmpty(raw.service_type),
    faceToFace,
    // Placeholders ('n/a', 'in person', …) are not a real sub-type — store null.
    nonFaceToFaceSessionType: hasTelehealthSubType(raw.non_face_to_face_session_type)
      ? trimOrEmpty(raw.non_face_to_face_session_type)
      : null,
    start: start.epochMs,
    end: end.epochMs,
    // Preserve the offset AHP sent on `start` (its `end` should match).
    timezone: start.offset,
    coreStatus,
    notes: typeof raw.notes === 'string' ? raw.notes : '',
    checkins: mapCheckins(raw.checkins),

    lifecycleState: deriveInitialLifecycle(coreStatus),
    actualStart: null,
    actualEnd: null,
    checkIn: null,
    checkOut: null,
    kmTracked: null,
    driveSegments: [],
    noteStatus: 'none',
  }
}

/**
 * Map an array of raw AHP sessions. Throws on the first invalid item — callers
 * that need per-item results (e.g. the inbound webhook) should iterate and call
 * {@link mapCoreSessionToDomain} inside their own try/catch instead.
 */
export function mapCoreSessionsToDomain(raw: RawCoreSession[]): Session[] {
  if (!Array.isArray(raw)) return []
  return raw.map(mapCoreSessionToDomain)
}

/** True when AHP has cancelled this session occurrence. */
export function isSessionCancelled(session: Pick<Session, 'coreStatus'>): boolean {
  return session.coreStatus === 'cancelled'
}
